/**
 * Gemeinsame Beschriftungen, Farben und Helfer für Aufgaben
 * (Aufgabenfenster, Aufgabenbaum, Aufgabenseite, Adressen und Projekte).
 */

export const AUFGABEN_STATUS = [
  { wert: 'open', label: 'Offen', klasse: 'bg-gray-100 text-gray-600' },
  { wert: 'in_progress', label: 'In Bearbeitung', klasse: 'bg-indigo-50 text-indigo-700' },
  { wert: 'waiting', label: 'Wartend', klasse: 'bg-orange-50 text-orange-700' },
  { wert: 'completed', label: 'Abgeschlossen', klasse: 'bg-emerald-50 text-emerald-700' },
  { wert: 'cancelled', label: 'Abgebrochen', klasse: 'bg-gray-100 text-gray-400' },
];
const STATUS_MAP = Object.fromEntries(AUFGABEN_STATUS.map((s) => [s.wert, s]));
// Ältere Statuswerte aus Importen/Vorlagen
STATUS_MAP.todo = STATUS_MAP.open;
STATUS_MAP.done = STATUS_MAP.completed;
STATUS_MAP.review = { wert: 'review', label: 'Prüfung', klasse: 'bg-sky-50 text-sky-700' };

export const statusInfo = (s) => STATUS_MAP[s] || STATUS_MAP.open;

export const AUFGABEN_PRIORITAET = [
  { wert: 'low', label: 'Niedrig', farbe: '#9CA3AF' },
  { wert: 'medium', label: 'Mittel', farbe: '#EAB308' },
  { wert: 'high', label: 'Hoch', farbe: '#F97316' },
  { wert: 'urgent', label: 'Dringend', farbe: '#DC2626' },
];
const PRIO_MAP = Object.fromEntries(AUFGABEN_PRIORITAET.map((p) => [p.wert, p]));
export const prioritaetInfo = (p) => PRIO_MAP[p] || PRIO_MAP.medium;

export const istErledigt = (t) => ['completed', 'done', 'cancelled'].includes(t?.status);

/** Lokales ISO-Datum (JJJJ-MM-TT) — nur intern */
export const isoHeute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Datum deutsch (TT.MM.JJJJ) */
export const deDatum = (wert) => {
  if (!wert) return '';
  const s = String(wert).slice(0, 10);
  const [y, m, d] = s.split('-');
  return d && m && y ? `${d}.${m}.${y}` : '';
};

export const istUeberfaellig = (t) => {
  if (!t?.due_date || istErledigt(t)) return false;
  return String(t.due_date).slice(0, 10) < isoHeute();
};

export const istHeuteFaellig = (t) => !!t?.due_date && !istErledigt(t) && String(t.due_date).slice(0, 10) === isoHeute();

export const ERINNERUNG_OPTIONEN = [
  { wert: '15min', label: '15 Minuten vorher' },
  { wert: '30min', label: '30 Minuten vorher' },
  { wert: '1h', label: '1 Stunde vorher' },
  { wert: '2h', label: '2 Stunden vorher' },
  { wert: '1d', label: '1 Tag vorher' },
  { wert: '1w', label: '1 Woche vorher' },
];

export const WIEDERHOLUNG_LABEL = {
  daily: 'Täglich', weekly: 'Wöchentlich', monthly: 'Monatlich',
  quarterly: 'Vierteljährlich', half_yearly: 'Halbjährlich', yearly: 'Jährlich',
};

export const adressName = (a) => {
  if (!a) return '';
  if (a.type === 'company' && a.company_name) return a.company_name;
  return [a.first_name, a.last_name].filter(Boolean).join(' ') || a.company_name || a.email || 'Unbekannt';
};

export const benutzerName = (u) => (u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '' : '');

export const initialen = (name) => String(name || '?').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

/**
 * Baut aus einer flachen Aufgabenliste (mit parent_task_id) einen Baum.
 * Aufgaben, deren übergeordnete Aufgabe nicht in der Liste steht, werden zu Wurzeln.
 */
export function baueBaum(aufgaben = []) {
  const knoten = new Map();
  aufgaben.forEach((t) => knoten.set(t.id, { ...t, kinder: [] }));
  const wurzeln = [];
  aufgaben.forEach((t) => {
    const k = knoten.get(t.id);
    const eltern = t.parent_task_id && t.parent_task_id !== t.id ? knoten.get(t.parent_task_id) : null;
    if (eltern) eltern.kinder.push(k); else wurzeln.push(k);
  });
  const sortiere = (liste) => {
    liste.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
    liste.forEach((k) => sortiere(k.kinder));
  };
  sortiere(wurzeln);
  return wurzeln;
}

/** Blätter eines Baumknotens (Aufgaben ohne eigene Unteraufgaben) */
export const blaetter = (k) => (k.kinder?.length ? k.kinder.flatMap(blaetter) : [k]);

/**
 * Fortschritt in Prozent: aus erledigten Unteraufgaben berechnet, sonst aus dem Status
 * bzw. dem gespeicherten progress_percentage.
 */
export function fortschritt(k) {
  if (k.kinder?.length) {
    const b = blaetter(k);
    return Math.round((b.filter(istErledigt).length / b.length) * 100);
  }
  if (k.subtask_count > 0) return Math.round(((k.subtask_completed || 0) / k.subtask_count) * 100);
  if (istErledigt(k)) return 100;
  if (typeof k.progress_percentage === 'number' && k.progress_percentage > 0) return k.progress_percentage;
  return 0;
}

/** Zähler „erledigt / gesamt“ der Unteraufgaben (Blätter), falls vorhanden */
export function unteraufgabenZaehler(k) {
  if (k.kinder?.length) {
    const b = blaetter(k);
    return { erledigt: b.filter(istErledigt).length, gesamt: b.length };
  }
  if (k.subtask_count > 0) return { erledigt: k.subtask_completed || 0, gesamt: k.subtask_count };
  return null;
}
